// Shared Raw Material resolution + filter logic.
//
// Feb 2026 — extracted from `pages/RMInward.js` so any RM-picking UI can
// reuse the same "type-to-resolve → pick from a capped shortlist" behaviour
// without independently re-implementing (and drifting from) it. The prior
// generation of this behaviour lived as inline helpers on RMInward.js
// (`filterRMsForLine` + the `handleLineItemChange` regex block); a separate
// re-implementation on `components/bill/RMSearchSelect.jsx` diverged and
// produced the prefix-collision wipe bug on the Job Work Receipt Finished
// RM field (typing `INP_01163` auto-confirmed on `INP_011` then wiped).
//
// Contract: pure functions, no React deps. Callers own their own state
// (RMInward keeps its `rm_search` / `rm_id` per-line pair; Job Work Receipt
// keeps a single `rm_search` / `rm_id` on the form).

// Regex used at the head of the typed value. Greedy `\d+` — captures the
// FULL numeric suffix (so `INP_01163` matches whole, not just `INP_011`).
export const RM_ID_PREFIX_REGEX = /^([A-Z]+_\d+)/;

// Synthetic FREIGHT entry — surfaced by RM Inward when the user searches
// "freight" / "fr". Callers that don't accept freight (e.g. Job Work
// Receipt's Finished RM) pass `includeFreight: false` and this stays out.
const FREIGHT_SYNTHETIC = {
  rm_id: "FREIGHT",
  category: "FREIGHT",
  category_data: { part_name: "Freight (service charge)" },
  is_freight: true,
};

// Resolve a typed value against the full RM catalog.
// - `typed`: the raw user-typed string (or a datalist-selected value).
// - `rms`: the complete RM catalog (NOT a pre-filtered shortlist).
// - `includeFreight`: when true, `"FREIGHT"` (case-insensitive) resolves
//   to the synthetic freight entry (RM Inward path). Job Work Receipt
//   passes false — freight is not a Finished RM.
//
// Returns the resolved catalog entry, or `null` when the typed value
// cannot be resolved. Never mutates inputs. Never "wipes" — a partial
// or unresolvable typed value simply returns `null`; the caller decides
// whether to clear its own `rm_id`.
export const resolveTypedRM = (typed, rms, { includeFreight = false } = {}) => {
  const v = (typed || "").trim();
  if (!v) return null;
  if (includeFreight && v.toUpperCase() === "FREIGHT") {
    return FREIGHT_SYNTHETIC;
  }
  const m = v.match(RM_ID_PREFIX_REGEX);
  if (!m) return null;
  const rmId = m[1];
  const list = Array.isArray(rms) ? rms : [];
  return list.find((r) => r.rm_id === rmId) || null;
};

// Filter the RM catalog for the shortlist that populates the <datalist>
// + fallback <select>. Byte-identical semantics to RM Inward's original
// `filterRMsForLine`:
//   - `search.length < 2` → return the first 50 of the full catalog
//     (browsing behaviour before the user has typed anything specific).
//   - `search.length >= 2` → substring match (case-insensitive) on
//     `rm_id`, `category`, and `category_data.part_name`; capped at 50.
//   - `includeFreight` → prepend the synthetic freight entry when the
//     search text loosely matches freight terms.
export const filterRMsForSearch = (search, rms, { includeFreight = false } = {}) => {
  const list = Array.isArray(rms) ? rms : [];
  const raw = search || "";
  const sLower = raw.toLowerCase().trim();
  const showFreight = includeFreight && sLower.length >= 2 && (
    "freight".includes(sLower) || sLower.includes("freight") || sLower === "fr"
  );
  if (raw.length >= 2) {
    const q = sLower;
    const matched = list.filter((rm) =>
      (rm.rm_id || "").toLowerCase().includes(q) ||
      (rm.category || "").toLowerCase().includes(q) ||
      ((rm.category_data && rm.category_data.part_name) || "").toLowerCase().includes(q)
    ).slice(0, 50);
    return showFreight ? [FREIGHT_SYNTHETIC, ...matched] : matched;
  }
  return list.slice(0, 50);
};
